import React, { useEffect, useState, memo } from 'react';
import { UserIcon, ShieldIcon, BellIcon, MonitorIcon, LinkIcon, SettingsIcon, LogOutIcon, CheckCircleIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useRole } from '../hooks/useRole';
type SettingsTab = 'profilo' | 'sicurezza' | 'notifiche' | 'interfaccia' | 'integrazioni' | 'account';
export function Settings() {
  const {
    role
  } = useRole();
  const [activeTab, setActiveTab] = useState<SettingsTab>('profilo');
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [autosaveInterval, setAutosaveInterval] = useState(localStorage.getItem('autosaveInterval') || '45');
  const showToast = (msg: string) => {
    setToastMessage(msg);
    setTimeout(() => setToastMessage(null), 3000);
  };
  const handleSave = () => {
    localStorage.setItem('autosaveInterval', autosaveInterval);
    showToast('Impostazioni salvate con successo');
  };
  const allTabs = [{
    id: 'profilo',
    label: 'Profilo',
    icon: UserIcon,
    roles: ['superadmin', 'medico', 'paziente']
  }, {
    id: 'sicurezza',
    label: 'Sicurezza',
    icon: ShieldIcon,
    roles: ['superadmin', 'medico', 'paziente']
  }, {
    id: 'notifiche',
    label: 'Notifiche',
    icon: BellIcon,
    roles: ['superadmin', 'medico', 'paziente']
  }, {
    id: 'interfaccia',
    label: 'Preferenze interfaccia',
    icon: MonitorIcon,
    roles: ['superadmin', 'medico', 'paziente']
  }, {
    id: 'integrazioni',
    label: 'Integrazioni',
    icon: LinkIcon,
    roles: ['superadmin']
  }, {
    id: 'account',
    label: 'Gestione account',
    icon: SettingsIcon,
    roles: ['superadmin', 'medico', 'paziente']
  }] as const;
  const filteredTabs = allTabs.filter(tab => tab.roles.includes(role));
  // Reset tab if current role doesn't have access to it
  useEffect(() => {
    if (!filteredTabs.find(t => t.id === activeTab)) {
      setActiveTab('profilo');
    }
  }, [role, activeTab, filteredTabs]);
  // Profile default data based on role
  const profileData = {
    superadmin: {
      nome: 'Superadmin',
      cognome: 'Sistema',
      email: 'admin@health1.it',
      ruoloLabel: 'Amministratore di sistema'
    },
    medico: {
      nome: 'Mario',
      cognome: 'Rossi',
      email: 'mario.rossi@health1.it',
      ruoloLabel: 'Medico Refertatore',
      specializzazione: 'Radiologia Diagnostica'
    },
    paziente: {
      nome: 'Giulia',
      cognome: 'Bianchi',
      email: 'giulia.bianchi@email.it',
      ruoloLabel: 'Paziente'
    }
  }[role];
  return <div className="space-y-6 w-full min-w-0 pb-12" data-id="element-2514">
      <div data-id="element-2515">
        <h1 className="text-2xl md:text-page-title mb-1 md:mb-2 text-text-primary font-bold" data-id="element-2516">
          Impostazioni
        </h1>
        <p className="text-sm md:text-body text-text-secondary" data-id="element-2517">
          Gestisci il tuo profilo, le preferenze e le configurazioni di sistema.
        </p>
      </div>

      <div className="flex flex-col md:flex-row gap-6" data-id="element-2518">
        {/* Left Navigation */}
        <div className="w-full md:w-64 shrink-0" data-id="element-2519">
          <nav className="flex flex-col space-y-1" data-id="element-2520">
            {filteredTabs.map(tab => <button key={tab.id} onClick={() => setActiveTab(tab.id as SettingsTab)} className={`flex items-center gap-3 px-4 py-3 text-sm font-medium rounded-medical transition-colors ${activeTab === tab.id ? 'bg-medical-green-50 text-medical-green' : 'text-text-secondary hover:bg-clinical-gray hover:text-text-primary'}`} data-id="element-2521">
                <tab.icon size={18} className={activeTab === tab.id ? 'text-medical-green' : 'text-text-tertiary'} data-id="element-2522" />
                {tab.label}
              </button>)}
          </nav>
        </div>

        {/* Content Area */}
        <div className="flex-1 bg-white rounded-medical-lg shadow-card border border-clinical-gray-100 p-6 md:p-8" data-id="element-2523">
          {activeTab === 'profilo' && <div className="space-y-6" data-id="element-2524">
              <h2 className="text-lg font-semibold text-text-primary border-b border-clinical-gray-100 pb-4" data-id="element-2525">
                Informazioni Profilo
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-2526">
                <div data-id="element-2527">
                  <label className="block text-sm font-medium text-text-secondary mb-1" data-id="element-2528">
                    Nome
                  </label>
                  <input type="text" defaultValue={profileData.nome} className="medical-input" data-id="element-2529" />
                </div>
                <div data-id="element-2530">
                  <label className="block text-sm font-medium text-text-secondary mb-1" data-id="element-2531">
                    Cognome
                  </label>
                  <input type="text" defaultValue={profileData.cognome} className="medical-input" data-id="element-2532" />
                </div>
                <div className="md:col-span-2" data-id="element-2533">
                  <label className="block text-sm font-medium text-text-secondary mb-1" data-id="element-2534">
                    Email
                  </label>
                  <input type="email" defaultValue={profileData.email} className="medical-input" data-id="element-2535" />
                </div>
                <div className="w-full" data-id="element-2536">
                  <label className="block text-sm font-medium text-text-secondary mb-1" data-id="element-2537">
                    Ruolo
                  </label>
                  <input type="text" defaultValue={profileData.ruoloLabel} disabled className="medical-input bg-clinical-gray-50 cursor-not-allowed " data-id="element-2538" />
                </div>
                {role === 'medico' && <div data-id="element-2539">
                    <label className="block text-sm font-medium text-text-secondary mb-1" data-id="element-2540">
                      Specializzazione
                    </label>
                    <input type="text" defaultValue={profileData.specializzazione} disabled className="medical-input bg-clinical-gray-50 cursor-not-allowed" data-id="element-2541" />
                  </div>}
              </div>
              <div className="pt-4" data-id="element-2542">
                <button onClick={handleSave} className="medical-btn-primary" data-id="element-2543">
                  Salva modifiche
                </button>
              </div>
            </div>}

          {activeTab === 'sicurezza' && <div className="space-y-6" data-id="element-2544">
              <h2 className="text-lg font-semibold text-text-primary border-b border-clinical-gray-100 pb-4" data-id="element-2545">
                Sicurezza e Password
              </h2>
              <div className="space-y-4 max-w-md" data-id="element-2546">
                <div data-id="element-2547">
                  <label className="block text-sm font-medium text-text-secondary mb-1" data-id="element-2548">
                    Password attuale
                  </label>
                  <input type="password" placeholder="••••••••" className="medical-input" data-id="element-2549" />
                </div>
                <div data-id="element-2550">
                  <label className="block text-sm font-medium text-text-secondary mb-1" data-id="element-2551">
                    Nuova password
                  </label>
                  <input type="password" placeholder="••••••••" className="medical-input" data-id="element-2552" />
                </div>
                <div data-id="element-2553">
                  <label className="block text-sm font-medium text-text-secondary mb-1" data-id="element-2554">
                    Conferma nuova password
                  </label>
                  <input type="password" placeholder="••••••••" className="medical-input" data-id="element-2555" />
                </div>
              </div>
              <div className="pt-4" data-id="element-2556">
                <button onClick={handleSave} className="medical-btn-primary" data-id="element-2557">
                  Aggiorna password
                </button>
              </div>

              <div className="mt-10 pt-6 border-t border-clinical-gray-100" data-id="element-2558">
                <h3 className="text-md font-semibold text-text-primary mb-2" data-id="element-2559">
                  Sessioni attive
                </h3>
                <p className="text-sm text-text-secondary mb-4" data-id="element-2560">
                  Gestisci i dispositivi attualmente connessi al tuo account.
                </p>
                <div className="bg-clinical-gray-50 p-4 rounded-medical border border-clinical-gray-200 flex justify-between items-center" data-id="element-2561">
                  <div data-id="element-2562">
                    <p className="text-sm font-medium text-text-primary" data-id="element-2563">
                      MacBook Pro - Chrome
                    </p>
                    <p className="text-xs text-text-tertiary" data-id="element-2564">
                      Milano, IT • Attivo ora
                    </p>
                  </div>
                  <span className="text-xs font-medium text-medical-green bg-medical-green-50 px-2 py-1 rounded-full" data-id="element-2565">
                    Sessione corrente
                  </span>
                </div>
              </div>
            </div>}

          {activeTab === 'notifiche' && <div className="space-y-6" data-id="element-2566">
              <h2 className="text-lg font-semibold text-text-primary border-b border-clinical-gray-100 pb-4" data-id="element-2567">
                Preferenze Notifiche
              </h2>
              <div className="space-y-4" data-id="element-2568">
                {/* SUPERADMIN NOTIFICATIONS */}
                {role === 'superadmin' && <>
                    <NotificationItem title="Notifiche piattaforma" description="Avvisi generali di sistema e aggiornamenti." data-id="element-2569" />
                    <NotificationItem title="Notifiche email" description="Riepilogo giornaliero delle attività amministrative." defaultChecked={false} data-id="element-2570" />
                    <NotificationItem title="Notifiche errori di sistema" description="Avvisi immediati su anomalie o blocchi infrastrutturali." data-id="element-2571" />
                    <NotificationItem title="Notifiche integrazione macchinari" description="Avvisi su errori o disconnessioni dei macchinari diagnostici." data-id="element-2572" />
                    <NotificationItem title="Notifiche import dati" description="Esito delle sincronizzazioni con sistemi esterni (es. CUP)." data-id="element-2573" />
                  </>}

                {/* MEDICO NOTIFICATIONS */}
                {role === 'medico' && <>
                    <NotificationItem title="Nuovi esami assegnati" description="Ricevi una notifica quando un nuovo paziente ti viene assegnato." data-id="element-2574" />
                    <NotificationItem title="Referti da completare" description="Promemoria per i referti in sospeso o da validare." data-id="element-2575" />
                    <NotificationItem title="Invio referti riuscito / fallito" description="Conferma dello stato di invio dei referti firmati." data-id="element-2576" />
                    <NotificationItem title="Notifiche email" description="Ricevi un riepilogo delle attività cliniche a fine giornata." defaultChecked={false} data-id="element-2577" />
                    <NotificationItem title="Notifiche piattaforma" description="Avvisi generali e comunicazioni interne." data-id="element-2578" />
                  </>}

                {/* PAZIENTE NOTIFICATIONS */}
                {role === 'paziente' && <>
                    <NotificationItem title="Disponibilità referto" description="Ricevi una notifica non appena il tuo referto è pronto per il download." data-id="element-2579" />
                    <NotificationItem title="Promemoria esami prenotati" description="Promemoria 24 ore prima del tuo appuntamento." data-id="element-2580" />
                    <NotificationItem title="Email piattaforma" description="Comunicazioni importanti e aggiornamenti via email." data-id="element-2581" />
                    <NotificationItem title="Notifiche piattaforma" description="Avvisi generali sul portale paziente." defaultChecked={false} data-id="element-2582" />
                  </>}
              </div>
              <div className="pt-4" data-id="element-2583">
                <button onClick={handleSave} className="medical-btn-primary" data-id="element-2584">
                  Salva preferenze
                </button>
              </div>
            </div>}

          {activeTab === 'interfaccia' && <div className="space-y-6" data-id="element-2585">
              <h2 className="text-lg font-semibold text-text-primary border-b border-clinical-gray-100 pb-4" data-id="element-2586">
                Preferenze Interfaccia
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-1 grid-cols-2 gap-6" data-id="element-2587">
                {/* ALL ROLES */}
                <div data-id="element-2588">
                  <label className="block text-sm font-medium text-text-secondary mb-1" data-id="element-2589">
                    Lingua
                  </label>
                  <select className="medical-input" data-id="element-2590">
                    <option data-id="element-2591">Italiano</option>
                    <option data-id="element-2592">English</option>
                  </select>
                </div>
                <div data-id="element-2593">
                  <label className="block text-sm font-medium text-text-secondary mb-1" data-id="element-2594">
                    Formato Data
                  </label>
                  <select className="medical-input" data-id="element-2595">
                    <option data-id="element-2596">DD/MM/YYYY</option>
                    <option data-id="element-2597">MM/DD/YYYY</option>
                  </select>
                </div>

                {/* SUPERADMIN ONLY */}
                {role === 'superadmin' && <div data-id="element-2598">
                    <label className="block text-sm font-medium text-text-secondary mb-1" data-id="element-2599">
                      Densità interfaccia
                    </label>
                    <select className="medical-input" data-id="element-2600">
                      <option data-id="element-2601">Standard</option>
                      <option data-id="element-2602">Compatta</option>
                    </select>
                  </div>}

                {/* SUPERADMIN & MEDICO */}
                {(role === 'superadmin' || role === 'medico') && <div data-id="element-2603">
                    <label className="block text-sm font-medium text-text-secondary mb-1" data-id="element-2604">
                      Intervallo salvataggio automatico editor
                    </label>
                    <select className="medical-input" value={autosaveInterval} onChange={e => setAutosaveInterval(e.target.value)} data-id="element-2605">
                      <option value="15" data-id="element-2606">15 secondi</option>
                      <option value="30" data-id="element-2607">30 secondi</option>
                      <option value="45" data-id="element-2608">45 secondi</option>
                      <option value="60" data-id="element-2609">60 secondi</option>
                      <option value="120" data-id="element-2610">2 minuti</option>
                    </select>
                  </div>}

                {/* MEDICO ONLY */}
                {role === 'medico' && <div data-id="element-2611">
                    <label className="block text-sm font-medium text-text-secondary mb-1" data-id="element-2612">
                      Layout refertazione predefinito
                    </label>
                    <select className="medical-input" data-id="element-2613">
                      <option data-id="element-2614">Standard (Singola colonna)</option>
                      <option data-id="element-2615">Avanzato (Doppia colonna)</option>
                      <option data-id="element-2616">Compatto</option>
                    </select>
                  </div>}
              </div>
              <div className="pt-4" data-id="element-2617">
                <button onClick={handleSave} className="medical-btn-primary" data-id="element-2618">
                  Salva preferenze
                </button>
              </div>
            </div>}

          {activeTab === 'integrazioni' && role === 'superadmin' && <div className="space-y-6" data-id="element-2619">
              <h2 className="text-lg font-semibold text-text-primary border-b border-clinical-gray-100 pb-4" data-id="element-2620">
                Integrazioni di Sistema
              </h2>
              <div className="space-y-4" data-id="element-2621">
                <div className="p-4 border border-clinical-gray-200 rounded-medical flex items-center justify-between" data-id="element-2622">
                  <div className="flex items-center gap-4" data-id="element-2623">
                    <div className="w-10 h-10 bg-blue-50 text-blue-600 rounded-lg flex items-center justify-center font-bold" data-id="element-2624">
                      PACS
                    </div>
                    <div data-id="element-2625">
                      <p className="text-sm font-medium text-text-primary" data-id="element-2626">
                        Sistema PACS Aziendale
                      </p>
                      <p className="text-xs text-text-secondary" data-id="element-2627">
                        Sincronizzazione immagini diagnostiche
                      </p>
                    </div>
                  </div>
                  <span className="text-xs font-medium text-medical-green bg-medical-green-50 px-2.5 py-1 rounded-full border border-medical-green/20" data-id="element-2628">
                    Connesso
                  </span>
                </div>
                <div className="p-4 border border-clinical-gray-200 rounded-medical flex items-center justify-between" data-id="element-2629">
                  <div className="flex items-center gap-4" data-id="element-2630">
                    <div className="w-10 h-10 bg-purple-50 text-purple-600 rounded-lg flex items-center justify-center font-bold" data-id="element-2631">
                      CUP
                    </div>
                    <div data-id="element-2632">
                      <p className="text-sm font-medium text-text-primary" data-id="element-2633">
                        CUP Regionale
                      </p>
                      <p className="text-xs text-text-secondary" data-id="element-2634">
                        Importazione automatica prenotazioni
                      </p>
                    </div>
                  </div>
                  <span className="text-xs font-medium text-medical-green bg-medical-green-50 px-2.5 py-1 rounded-full border border-medical-green/20" data-id="element-2635">
                    Connesso
                  </span>
                </div>
              </div>
            </div>}

          {activeTab === 'account' && <div className="space-y-6" data-id="element-2636">
              <h2 className="text-lg font-semibold text-text-primary border-b border-clinical-gray-100 pb-4" data-id="element-2637">
                Gestione Account
              </h2>
              <div className="space-y-4" data-id="element-2638">
                <div className="p-4 bg-clinical-gray-50 rounded-medical border border-clinical-gray-200" data-id="element-2639">
                  <h3 className="text-sm font-medium text-text-primary mb-1" data-id="element-2640">
                    Disconnetti ovunque
                  </h3>
                  <p className="text-xs text-text-secondary mb-3" data-id="element-2641">
                    Termina tutte le sessioni attive su tutti i dispositivi.
                  </p>
                  <button className="medical-btn-secondary text-sm py-1.5" data-id="element-2642">
                    Disconnetti tutte le sessioni
                  </button>
                </div>

                <div className="p-4 bg-status-error-bg rounded-medical border border-status-error/20" data-id="element-2643">
                  <h3 className="text-sm font-medium text-status-error mb-1" data-id="element-2644">
                    Zona Pericolosa
                  </h3>
                  <p className="text-xs text-status-error/80 mb-3" data-id="element-2645">
                    Le azioni in questa area possono avere conseguenze
                    permanenti sul sistema.
                  </p>
                  <button className="px-4 py-2 bg-status-error text-white text-sm font-medium rounded-medical hover:bg-status-error/90 transition-colors" data-id="element-2646">
                    Richiedi disattivazione account
                  </button>
                </div>
              </div>
            </div>}
        </div>
      </div>

      {/* Toast */}
      <AnimatePresence data-id="element-2647">
        {toastMessage && <motion.div initial={{
        opacity: 0,
        y: 50,
        scale: 0.9
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 20,
        scale: 0.9
      }} className="fixed bottom-4 left-1/2 -translate-x-1/2 sm:left-auto sm:translate-x-0 sm:right-6 sm:bottom-6 z-50 flex items-center gap-3 px-4 py-3 rounded-medical-lg shadow-modal border border-status-success/20 bg-white w-[calc(100%-2rem)] max-w-[calc(100%-2rem)] sm:w-auto sm:max-w-md" data-id="element-2648">
            <CheckCircleIcon className="text-status-success shrink-0" size={20} data-id="element-2649" />
            <span className="text-sm font-medium text-text-primary" data-id="element-2650">
              {toastMessage}
            </span>
          </motion.div>}
      </AnimatePresence>
    </div>;
}
// Helper component for Notification items
function NotificationItem({
  title,
  description,
  defaultChecked = true
}: {
  title: string;
  description: string;
  defaultChecked?: boolean;
}) {
  return <div className="flex items-center justify-between p-4 border border-clinical-gray-100 rounded-medical" data-id="element-2651">
      <div data-id="element-2652">
        <p className="text-sm font-medium text-text-primary" data-id="element-2653">{title}</p>
        <p className="text-xs text-text-secondary" data-id="element-2654">{description}</p>
      </div>
      <input type="checkbox" defaultChecked={defaultChecked} className="w-4 h-4 text-medical-green rounded border-clinical-gray-300 focus:ring-medical-green" data-id="element-2655" />
    </div>;
}